import axios from 'axios';
import { GenericReport } from 'bindings/reports/generic/GenericReport';
import { Cookies } from 'react-cookie';
import { atom, selector } from 'recoil';
import { URL_FOUNDATION } from '../config/keys';
import { AtomCategory, AtomState } from './utils/model/Model';
import { AVisibleCompanyFieldIds, visibleOf } from './additionalColumns';
import { GroupInsideType } from '../components_v2/Selector/actions';
import { Id } from '../containers_v2/settings/subSettings/statusesAndTagsSettings';
import { ColumnDisplayOptions } from '../containers_v2/map/colorScale';

export enum CalculatedFieldTarget {
	Company = 'Company',
	External = 'External',
	CompanyProduct = 'CompanyProduct',
	/// a perfect-store scorecard: its report holds criteria, not panels, and
	/// the report editor cannot represent it
	Score = 'Score',
	/// objective completion: its report names the goals to read, and the value
	/// is what a company has done against what it was asked to do
	Objective = 'Objective',
	/// one value per promotion, from the forms filled for it
	Promotion = 'Promotion',
	/// one value per promotion x product
	PromotionProduct = 'PromotionProduct',
}

/// Calculated fields of the promotions: they hold no company value, so the
/// company pages, the map and the reports leave them out
export function isPromotionCalculatedField(cf: { target: CalculatedFieldTarget }): boolean {
	return cf.target === CalculatedFieldTarget.Promotion || cf.target === CalculatedFieldTarget.PromotionProduct;
}

export type CalculatedField = {
	id: number;
	target: CalculatedFieldTarget;
	field_name: string;
	report: GenericReport;
	deleted?: boolean;
	ishidded: boolean;
	// how the map shows the values
	display_options?: ColumnDisplayOptions;
};

export type CompanyCalculatedField = [Id, object];

/// Fields storing one value per product ({ products: [[uuid, value], ...] }):
/// classic company x product fields, and external KPIs with the per-product knob
export function isPerProductCalculatedField(cf: CalculatedField): boolean {
	if (cf.target === CalculatedFieldTarget.CompanyProduct) return true;
	const external = (cf.report as unknown as { external?: { per_product?: boolean } })?.external;
	return cf.target === CalculatedFieldTarget.External && !!external?.per_product;
}

// -----------------------[ ATOM ]----------------------- //
const AGlobalCalculatedFields: AtomState<CalculatedField[]> = {
	category: AtomCategory.GLOBAL,
	atom: atom({
		key: 'atom_global_calculated_fields', // UNIQUE ID
		default: getCalculatedFields()
	})
};

/// The company calculated fields: every page of the companies reads these
export const ACalculatedFields = selector<CalculatedField[]>({
	key: 'ACalculatedFields',
	get: async({ get }) => get(AGlobalCalculatedFields.atom).filter(cf => !isPromotionCalculatedField(cf)),
	// a company page sets back the company fields only: the promotion fields
	// are kept; the settings page sets the whole list, kept as it is
	set: ({ set, get }, newValue) => {
		const list = Array.isArray(newValue) ? newValue : [];
		set(AGlobalCalculatedFields.atom, list.some(isPromotionCalculatedField)
			? list
			: [...list, ...get(AGlobalCalculatedFields.atom).filter(isPromotionCalculatedField)]);
	},
});

/// The calculated fields of the promotions and of their products
export const APromotionCalculatedFields = selector<CalculatedField[]>({
	key: 'APromotionCalculatedFields',
	get: ({ get }) => get(AGlobalCalculatedFields.atom).filter(cf => isPromotionCalculatedField(cf) && !cf.deleted),
});

/// The calculated fields this user may see on the company sheet: see
/// AVisibleAdditionalColumns for the two things that hide one.
export const AVisibleCalculatedFields = selector({
	key: 'AVisibleCalculatedFields',
	get: ({ get }) =>
		visibleOf(get(AVisibleCompanyFieldIds), GroupInsideType.CalculatedField, get(ACalculatedFields), c => c.id, c => c.ishidded),
});

// -----------------------[ API ]------------------------ //
const cookies = new Cookies();
const token: string | null = cookies.get('id_token') || null;

axios.defaults.headers.common.Authorization = `${token ?? ''}`;

export async function getCalculatedFields(): Promise<CalculatedField[]> {
	const res = await axios.get<CalculatedField[]>(`${URL_FOUNDATION}/api/v2/calculated-fields`).then(res => res.data);
	return res;
}

export async function postCalculatedFields(data: Omit<CalculatedField, 'id'>): Promise<number> {
	const res = await axios.post<number>(`${URL_FOUNDATION}/api/v2/calculated-fields`, data).then(res => res.data);
	return res;
}

export async function getCalculatedFieldsByCompany(clientCompanyId: number): Promise<CompanyCalculatedField[]> {
	const res = await axios.get<CompanyCalculatedField[]>(`${URL_FOUNDATION}/api/v2/calculated-fields/client-company/${clientCompanyId}`).then(res => res.data);
	return res;
}

/// What a scorecard made of one company: the share it earned, the points
/// behind it, and the criteria that produced them. `points` is null for a
/// criterion that did not apply — its weight left the total rather than
/// scoring zero — and `coverage` is the share of the card that applied.
export type CriterionScore = {
	name: string,
	field: number,
	value: number | null,
	points: number | null,
	weight: number,
};

export type ScorePenalty = {
	name: string,
	field: number,
	points: number,
};

export type Scored = {
	score: number | null,
	earned: number,
	earnable: number,
	coverage: number,
	breakdown: CriterionScore[],
	penalties: ScorePenalty[],
};

export type CompanyScore = {
	field_id: number,
	field_name: string,
	scored: Scored,
	computed_at: string,
};

export type UserScore = {
	field_id: number,
	field_name: string,
	score: number | null,
	earned: number,
	earnable: number,
	scored_companies: number,
	computed_at: string,
};

export async function getCompanyScores(clientCompanyId: number): Promise<CompanyScore[]> {
	return axios.get<CompanyScore[]>(`${URL_FOUNDATION}/api/v2/scores/client-company/${clientCompanyId}`).then(res => res.data);
}

export async function getUserScores(userId: number): Promise<UserScore[]> {
	return axios.get<UserScore[]>(`${URL_FOUNDATION}/api/v2/scores/user/${userId}`).then(res => res.data);
}

/// Where a field's values sit, for writing a threshold against the estate
/// rather than against an intuition. `unreadable` counts the values the scorer
/// cannot turn into a number: a criterion on those will never apply.
export type FieldDistribution = {
	field_id: number,
	companies: number,
	unreadable: number,
	min: number | null,
	p10: number | null,
	p50: number | null,
	p90: number | null,
	max: number | null,
};

export async function getFieldDistribution(fieldId: number): Promise<FieldDistribution> {
	return axios.get<FieldDistribution>(`${URL_FOUNDATION}/api/v2/scores/field/${fieldId}/distribution`).then(res => res.data);
}

/// What an objective field can be written against: a report holding goals the
/// pass could read, and what those goals look like under each of its columns.
/// `per_company` is how many of them end on a client company — the others
/// measure a dimension a per-company completion cannot come from.
export type ObjectiveColumn = {
	name: string,
	granularity: string,
	goals: number,
	per_company: number,
};

export type ObjectiveSource = {
	report: number,
	name: string,
	/// the granularity the report is ventilated at: the one its goals are keyed
	/// at, and the one the completion is measured against
	ventilation: string | null,
	columns: ObjectiveColumn[],
};

export async function getObjectiveSources(): Promise<ObjectiveSource[]> {
	return axios.get<ObjectiveSource[]>(`${URL_FOUNDATION}/api/v2/goals/objective-sources`).then(res => res.data);
}
